import { Injectable, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { ChannelService } from '@app/chat/services/channel.service';
import { ContactService } from '@app/contacts/services/contact.service';
import { DndService } from '@app/contacts/services/dnd.service';
import { EventName } from '@app/core/models/event-bus.models';
import { DEFAULT_SHORTCUT_BINDINGS, ShortcutAction } from '@app/core/models/keyboard-shortcut.models';
import { EventBusService } from '@app/core/services/event-bus.service';
import { CallParkService } from '@app/parking/services/call-park.service';
import { Call } from '@app/phone/models/call.model';
import { CompanionCall } from '@app/phone/models/companion.models';
import { PhoneService } from '@app/phone/services/phone.service';
import { RecordingService } from '@app/phone/services/recording.service';
import { SMSConversationStorageService } from '@app/sms/services/sms-conversation-storage.service';
import { SMSUnreadMessageService } from '@app/sms/services/sms-unread-message.service';
import { UnreadMessageService as ChatUnreadMessageService } from '@app/sms/services/unread-message.service';

const IGNORED_TARGET_TAGS = ['INPUT', 'TEXTAREA'];

/** Builds a canonical "Ctrl+Shift+Alt+Meta+KEY" string from a keydown event, in a fixed modifier order. */
export function normalizeKeyCombo(event: KeyboardEvent): string {
  const modifiers: string[] = [];
  if (event.ctrlKey) {
    modifiers.push('Ctrl');
  }
  if (event.shiftKey) {
    modifiers.push('Shift');
  }
  if (event.altKey) {
    modifiers.push('Alt');
  }
  if (event.metaKey) {
    modifiers.push('Meta');
  }

  // Modifier combos can produce a shifted/composed symbol in event.key instead of the base character
  // (e.g. Shift+4 -> "$", macOS Alt+A -> "å"/"Å"). event.code identifies the physical key regardless
  // of modifiers, so use it to recover the digit/letter.
  const digitKey = event.code.match(/^Digit(\d)$/)?.[1];
  const letterKey = event.code.match(/^Key([A-Z])$/)?.[1];
  const key = digitKey ?? letterKey ?? (event.key.length === 1 ? event.key.toUpperCase() : event.key);
  return [...modifiers, key].join('+');
}

@Injectable({
  providedIn: 'root',
})
export class KeyboardShortcutService implements OnDestroy {
  private readonly handleKeydown = (event: KeyboardEvent): void => {
    const target = event.target as HTMLElement | null;
    if (target && (IGNORED_TARGET_TAGS.includes(target.tagName) || target.isContentEditable)) {
      return;
    }

    const combo = normalizeKeyCombo(event);
    const action = this.getActionForCombo(combo);
    if (!action) {
      return;
    }

    event.preventDefault();
    this.dispatch(action);
  };

  constructor(
    private phoneService: PhoneService,
    private dndService: DndService,
    private recordingService: RecordingService,
    private eventBusService: EventBusService,
    private callParkService: CallParkService,
    private channelService: ChannelService,
    private smsConversationStorageService: SMSConversationStorageService,
    private smsUnreadMessageService: SMSUnreadMessageService,
    private chatUnreadMessageService: ChatUnreadMessageService,
    private contactService: ContactService,
    private router: Router
  ) {
    document.addEventListener('keydown', this.handleKeydown);
  }

  public ngOnDestroy(): void {
    document.removeEventListener('keydown', this.handleKeydown);
  }

  public getBinding(action: ShortcutAction): string | undefined {
    return DEFAULT_SHORTCUT_BINDINGS[action];
  }

  private getActionForCombo(combo: string): ShortcutAction | undefined {
    return (Object.keys(DEFAULT_SHORTCUT_BINDINGS) as ShortcutAction[]).find(
      (action) => DEFAULT_SHORTCUT_BINDINGS[action] === combo
    );
  }

  private dispatch(action: ShortcutAction): void {
    switch (action) {
      case ShortcutAction.AnswerCall: {
        const incomingCalls = this.phoneService.incomingCalls;
        const mostRecentIncomingCall = incomingCalls.at(-1);
        if (mostRecentIncomingCall) {
          this.phoneService.answerIncomingCall(mostRecentIncomingCall.id);
        }
        break;
      }
      case ShortcutAction.HangUp:
        this.phoneService.endCurrentVisibleCall();
        break;
      case ShortcutAction.ToggleMute: {
        const call = this.phoneService.currentVisibleCall;
        if (call && !(call instanceof CompanionCall)) {
          this.phoneService.toggleCallMuted(call.id);
        }
        break;
      }
      case ShortcutAction.ToggleHold: {
        const call = this.phoneService.currentVisibleCall;
        if (call) {
          this.phoneService.toggleCallHold(call.id);
        }
        break;
      }
      case ShortcutAction.ParkCall: {
        const call = this.phoneService.currentVisibleCall;
        if (call instanceof Call || call instanceof CompanionCall) {
          this.phoneService.parkCall(call, false);
          break;
        }

        const lotsWithParkedCalls = this.callParkService.getData().filter((lot) => lot.parkedCalls.length > 0);
        if (lotsWithParkedCalls.length === 1 && lotsWithParkedCalls[0].parkedCalls.length === 1) {
          this.phoneService.pickupParkedCall(lotsWithParkedCalls[0].id);
        }
        break;
      }
      case ShortcutAction.ToggleRecording: {
        const call = this.phoneService.currentVisibleCall;
        if (call instanceof Call) {
          this.recordingService.isCallRecording(call.id)
            ? this.recordingService.stopRecording(call.id)
            : this.recordingService.startRecording(call.id);
        }
        break;
      }
      case ShortcutAction.MergeCalls:
        this.phoneService.mergeVisibleCalls();
        break;
      case ShortcutAction.OpenDialpad:
        this.eventBusService.emit(EventName.OpenDialer, { number: undefined });
        break;
      case ShortcutAction.ToggleDnd:
        this.dndService.toggleDnd();
        break;
      case ShortcutAction.NewMessage:
        this.router.navigate(['/sms/all/new/']);
        break;
      case ShortcutAction.SwitchToCalls:
      case ShortcutAction.JumpToActiveCall:
        this.router.navigate(['/calls']);
        break;
      case ShortcutAction.SwitchToMessages:
        this.router.navigate(['/sms']);
        break;
      case ShortcutAction.SwitchToVoicemail:
        this.router.navigate(['/voicemail']);
        break;
      case ShortcutAction.SwitchToContacts:
        this.router.navigate(['/contacts']);
        break;
      case ShortcutAction.AttendedTransfer:
        this.eventBusService.emit(EventName.OpenTransferPanel);
        break;
      case ShortcutAction.ReplyToLastConversation: {
        if (this.router.url.startsWith('/chat')) {
          const channel = this.channelService.getData()[0];
          if (channel) {
            this.router.navigate(['/chat', channel.id]);
          }
        } else if (this.router.url.startsWith('/sms')) {
          const conversation = this.smsConversationStorageService.getAllSortedItems()[0];
          if (conversation) {
            this.router.navigate(['/sms', conversation.local, conversation.conversationId]);
          }
        }
        break;
      }
      case ShortcutAction.MarkConversationRead: {
        if (this.router.url.startsWith('/chat')) {
          const channel = this.channelService.getData()[0];
          if (channel) {
            this.chatUnreadMessageService.updateReadMarker(channel.channelArn);
          }
        } else if (this.router.url.startsWith('/sms')) {
          const conversation = this.smsConversationStorageService.getAllSortedItems()[0];
          if (conversation) {
            this.smsUnreadMessageService.updateReadMarker(conversation.conversationId);
          }
        }
        break;
      }
      case ShortcutAction.CallSelectedContact: {
        const [surface, contactId] = this.router.url.split('?')[0].split('/').filter(Boolean);
        if (surface === 'contacts' && contactId) {
          const contact = this.contactService.contactById(contactId);
          // Same precedence PhoneContactListComponent.getPrimaryContactNumber uses: extension first, then first tel.
          const number = contact?.ext ?? contact?.tels[0]?.number;
          if (number) {
            this.phoneService.call(number);
          }
        }
        break;
      }
      case ShortcutAction.VoicemailPlayPause:
        this.eventBusService.emit(EventName.PlayPauseSelectedVoicemail);
        break;
      case ShortcutAction.VoicemailSkipForward:
        this.eventBusService.emit(EventName.SkipSelectedVoicemailForward);
        break;
      case ShortcutAction.VoicemailSkipBack:
        this.eventBusService.emit(EventName.SkipSelectedVoicemailBack);
        break;
      case ShortcutAction.VoicemailDelete:
        this.eventBusService.emit(EventName.DeleteSelectedVoicemail);
        break;
      case ShortcutAction.VoicemailCallBack:
        this.eventBusService.emit(EventName.CallBackSelectedVoicemailSender);
        break;
      case ShortcutAction.FocusSearch:
        this.eventBusService.emit(EventName.FocusSearch);
        break;
      default:
        console.warn(`[KEYBOARD-SHORTCUT] no handler implemented for action "${action}"`);
    }
  }
}
